import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

type TruncatedTextProps = {
  text: string | null | undefined;
  className?: string;
  /** Optional accessible name when the visible label differs. */
  "aria-label"?: string;
};

/**
 * Single-line ellipsis text. When overflow is detected, exposes the complete
 * value via the native `title` attribute (browser light-gray tooltip) only —
 * never antd/custom black tooltips, so operators see exactly one tooltip.
 * Keyboard focus is enabled only while truncated.
 */
export function isOverflowing(el: HTMLElement | null): boolean {
  if (!el) return false;
  return el.scrollWidth > el.clientWidth + 1;
}

const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  className,
  "aria-label": ariaLabel,
}) => {
  const value = typeof text === "string" ? text : text == null ? "" : String(text);
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  const measure = useCallback(() => {
    setTruncated(isOverflowing(ref.current));
  }, []);

  useLayoutEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return undefined;
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => measure()) : null;
    ro?.observe(el);
    if (el.parentElement) ro?.observe(el.parentElement);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [value, className, measure]);

  return (
    <span
      ref={ref}
      className={className}
      data-testid="truncated-text"
      data-truncated={truncated ? "true" : "false"}
      data-full-text={value}
      data-tooltip-source={truncated && value ? "native-title" : "none"}
      title={truncated && value ? value : undefined}
      tabIndex={truncated && value ? 0 : undefined}
      aria-label={ariaLabel || (truncated && value ? value : undefined)}
    >
      {value}
    </span>
  );
};

export default TruncatedText;
